import { useState, useEffect, useRef } from 'react';
import type { DefaultTagDefinition, TagFilters } from '../../../../types/tags';
import {
  countActiveFilters,
  isJucoYearFacetEnabled,
  isReservedFilterKey,
  pruneJucoYearFacet,
  GRAD_YEAR_FILTER_KEY,
  JUCO_YEAR_FILTER_KEY,
  PLAYER_LEVEL_FILTER_KEY,
} from '../../../../types/tags';
import {
  JUCO_YEAR_FACET_HINT,
  JUCO_YEAR_FILTER_OPTIONS,
  PLAYER_LEVEL_FILTER_OPTIONS,
} from '../../../../constants/prospectFilterFacets';
import { FilterChip } from './FilterChip';
import { useDialogFocus } from '../../../../hooks/useDialogFocus';
import { Button } from '../../../../components/Button';
import { ModalCloseButton } from '../../../../components/ModalCloseButton';
import { tourAnchor } from '../../../tours/tourAnchors';
import './TagFilterModal.css';
import './FilterChip.css';

interface TagFilterModalProps {
  isOpen: boolean;
  definitions: DefaultTagDefinition[];
  /** Distinct graduation years present in the org's prospects (most-recent-first). */
  gradYearOptions: number[];
  currentFilters: TagFilters;
  onApply: (filters: TagFilters) => void;
  onReset: () => void;
  onClose: () => void;
  onManageTags?: () => void;
}

export const TagFilterModal = ({
  isOpen,
  definitions,
  gradYearOptions,
  currentFilters,
  onApply,
  onReset,
  onClose,
  onManageTags,
}: TagFilterModalProps) => {
  const [localFilters, setLocalFilters] = useState<TagFilters>(currentFilters);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Update local filters when modal opens with new current filters
  useEffect(() => {
    if (isOpen) {
      setLocalFilters(currentFilters);
    }
  }, [isOpen, currentFilters]);

  /*
    Escape, the Tab trap and focus restore. This dialog declares `aria-modal="true"`, both for
    screen readers and so the guided tour can see it: the tour's back walk refuses to step out
    of an open dialog by looking for `[role="dialog"][aria-modal="true"]`, and while this modal
    did not declare itself, Back could land the coach on a step spotlighting the toolbar behind
    it. The claim has to be true, so the trap comes with it rather than the attribute alone.
  */
  useDialogFocus({ isOpen, dialogRef, onClose });

  if (!isOpen) return null;

  const handleToggle = (tagName: string, value: string, isSingleSelect: boolean) => {
    setLocalFilters((prev) => {
      const current = prev[tagName] || [];
      let next: TagFilters;

      if (isSingleSelect) {
        // Single-select: clicking selected value clears it, clicking unselected value replaces
        const newValues = current.includes(value) ? [] : [value];
        if (newValues.length === 0) {
          const { [tagName]: _, ...rest } = prev;
          next = rest;
        } else {
          next = { ...prev, [tagName]: newValues };
        }
      } else {
        // Multi-select: toggle individual value
        const newValues = current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value];

        if (newValues.length === 0) {
          const { [tagName]: _, ...rest } = prev;
          next = rest;
        } else {
          next = { ...prev, [tagName]: newValues };
        }
      }

      // Dropping JUCO from Level strands any JC Year selection: the facets AND, so the
      // pair would guarantee zero rows. Clear it as Level changes rather than leaving a
      // hidden selection behind the now-inert facet.
      return pruneJucoYearFacet(next);
    });
  };

  const handleApply = () => {
    // Clean up empty arrays before applying
    const cleanedFilters = Object.entries(localFilters).reduce((acc, [key, values]) => {
      if (values && values.length > 0) {
        acc[key] = values;
      }
      return acc;
    }, {} as TagFilters);

    // Belt and braces: handleToggle already prunes, but `currentFilters` can arrive from
    // a stale URL that pairs a JC Year with no JUCO level.
    onApply(pruneJucoYearFacet(cleanedFilters));
  };

  const handleReset = () => {
    setLocalFilters({});
    onReset();
  };

  const activeCount = countActiveFilters(localFilters);
  // A tag category sharing a reserved key's name is dropped rather than rendered: both
  // sections would write to the same `TagFilters` key, and the org tag's option strings
  // would then be sent as `playerLevel` / `jucoYear` / `gradYear` and rejected with a
  // 400. Same collision `playersTableColumns.ts` special-cases for the "Class" tag.
  const sortedDefinitions = [...definitions]
    .filter((def) => !isReservedFilterKey(def.tagName))
    .sort((a, b) => a.displayOrder - b.displayOrder);
  const jucoYearEnabled = isJucoYearFacetEnabled(localFilters);
  const selectedLevels = localFilters[PLAYER_LEVEL_FILTER_KEY] || [];
  const selectedJucoYears = localFilters[JUCO_YEAR_FILTER_KEY] || [];

  return (
    <div
      className="tag-filter-modal-overlay"
      /*
        Dismiss only on the backdrop itself. This used to be a bare `onClick={onClose}` with a
        `stopPropagation` handler on the dialog to hold clicks back, which is the same behaviour
        — but a click handler on the `role="dialog"` element trips
        no-noninteractive-element-interactions, and the guard belongs on the backdrop that
        actually dismisses rather than on every element inside it.
      */
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="tag-filter-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tag-filter-modal-title"
      >
        {/* Header */}
        <div className="tag-filter-modal__header">
          <div>
            <h2 id="tag-filter-modal-title" className="tag-filter-modal__title">
              Filter Prospects
            </h2>
            <p className="tag-filter-modal__subtitle">
              {activeCount > 0
                ? `${activeCount} filter${activeCount === 1 ? '' : 's'} selected`
                : 'Select filters to narrow results'}
            </p>
          </div>
          <div className="tag-filter-modal__header-actions">
            {onManageTags && (
              <button
                type="button"
                className="tag-filter-modal__manage-tags"
                onClick={onManageTags}
                aria-label="Manage tag categories"
                {...tourAnchor('players.filter.manage-tags')}
              >
                Manage Tags
              </button>
            )}
            <ModalCloseButton onClick={onClose} {...tourAnchor('players.filter.close')} />
          </div>
        </div>

        {/* Body */}
        <div className="tag-filter-modal__body" {...tourAnchor('players.filter.options')}>
          {/* Grad Year — a real filter on the scalar gradYear field, not a tag.
              Rendered first and only when the org has prospects with grad years. */}
          {gradYearOptions.length > 0 && (
            <div className="tag-filter-modal__section">
              <div className="tag-filter-modal__section-header">
                <h3 className="tag-filter-modal__section-title">Grad Year</h3>
                <p className="tag-filter-modal__section-subtitle">Select multiple</p>
              </div>
              <div className="tag-filter-modal__chips">
                {gradYearOptions.map((year) => {
                  const value = String(year);
                  const selected = (localFilters[GRAD_YEAR_FILTER_KEY] || []).includes(value);
                  return (
                    <FilterChip
                      key={value}
                      value={value}
                      isSelected={selected}
                      onClick={() => handleToggle(GRAD_YEAR_FILTER_KEY, value, false)}
                      isSingleSelect={false}
                    />
                  );
                })}
              </div>
            </div>
          )}
          {/* Level — the scalar playerLevel column, a fixed three-value vocabulary, so the
              options are static rather than fetched. */}
          <div className="tag-filter-modal__section">
            <div className="tag-filter-modal__section-header">
              <h3 className="tag-filter-modal__section-title" id="filter-level-label">
                Level
              </h3>
              <p className="tag-filter-modal__section-subtitle">Select multiple</p>
            </div>
            <div
              className="tag-filter-modal__chips"
              role="group"
              aria-labelledby="filter-level-label"
            >
              {PLAYER_LEVEL_FILTER_OPTIONS.map((option) => (
                <FilterChip
                  key={option.value}
                  value={option.value}
                  label={option.label}
                  isSelected={selectedLevels.includes(option.value)}
                  onClick={() =>
                    handleToggle(PLAYER_LEVEL_FILTER_KEY, option.value, false)
                  }
                  isSingleSelect={false}
                />
              ))}
            </div>
          </div>

          {/* JC Year — inert unless Level includes JUCO, because the server ANDs across
              facets and the pair would match nobody. Kept visible with the reason
              stated: a control that vanishes is a control the coach hunts for. */}
          <div className="tag-filter-modal__section">
            <div className="tag-filter-modal__section-header">
              <h3 className="tag-filter-modal__section-title" id="filter-jc-year-label">
                JC Year
              </h3>
              <p
                className={`tag-filter-modal__section-subtitle${
                  jucoYearEnabled ? '' : ' tag-filter-modal__section-subtitle--hint'
                }`}
                id="filter-jc-year-hint"
              >
                {jucoYearEnabled ? 'Select multiple' : JUCO_YEAR_FACET_HINT}
              </p>
            </div>
            <div
              className="tag-filter-modal__chips"
              role="group"
              aria-labelledby="filter-jc-year-label"
              aria-describedby="filter-jc-year-hint"
            >
              {JUCO_YEAR_FILTER_OPTIONS.map((option) => (
                <FilterChip
                  key={option.value}
                  value={option.value}
                  label={option.label}
                  isSelected={selectedJucoYears.includes(option.value)}
                  onClick={() => handleToggle(JUCO_YEAR_FILTER_KEY, option.value, false)}
                  isSingleSelect={false}
                  disabled={!jucoYearEnabled}
                />
              ))}
            </div>
          </div>

          {sortedDefinitions.map((def) => {
            const isSingleSelect = def.tagType === 'single-select';
            const selectedValues = localFilters[def.tagName] || [];

            return (
              <div key={def.tagName} className="tag-filter-modal__section">
                <div className="tag-filter-modal__section-header">
                  <h3 className="tag-filter-modal__section-title">{def.tagName}</h3>
                  <p className="tag-filter-modal__section-subtitle">
                    {isSingleSelect ? 'Select one' : 'Select multiple'}
                  </p>
                </div>
                <div className="tag-filter-modal__chips">
                  {def.options.map((option) => (
                    <FilterChip
                      key={option}
                      value={option}
                      isSelected={selectedValues.includes(option)}
                      onClick={() => handleToggle(def.tagName, option, isSingleSelect)}
                      isSingleSelect={isSingleSelect}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="tag-filter-modal__footer">
          <button
            type="button"
            className="tag-filter-modal__reset"
            onClick={handleReset}
          >
            Reset
          </button>
          <Button
            variant="primary"
            onClick={handleApply}
            {...tourAnchor('players.filter.apply')}
          >
            {activeCount > 0 ? `Apply (${activeCount})` : 'Apply'}
          </Button>
        </div>
      </div>
    </div>
  );
};
